import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { StarRatingComponent } from 'ng-starrating';
import { combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, pluck, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { ErrorHandlingService } from 'src/app/services/error-handling.service';
import { LmoOrdersVerifyLoadDetailsDialogComponent } from '~lmo/components';
import {
  isComplete,
  isCompleteGroup,
  isDispatched,
  isDriverAccepted,
  isDriverRejected,
  isEtaArrived,
  isEtaNone,
  isEtaStaging,
  isFinished,
  isInProgressGroup,
  isLmoEditable,
  isPending,
  isPendingGroup,
  Order,
} from '~lmo/models/order.model';
import { isOnline, isUnreachable } from '~lmo/models/user.model';
import { LmoOrdersService } from '~lmo/services';
import { LmoFracFctvsService } from '~lmo/services/lmo-frac-fctvs.service';
import { LmoFracsService } from '~lmo/services/lmo-fracs.service';
import { FileUploadService } from '~services/api/file-upload.service';
import { HelperService } from '~services/helper.service';
import { RouterStateService } from '~services/router-state.service';
import { trackById } from '~utilities/trackById';
import { LmoOrdersCancelDialogComponent } from '../lmo-orders-cancel-dialog/lmo-orders-cancel-dialog.component';
import { LmoOrdersCompleteDialogComponent } from '../lmo-orders-complete-dialog/lmo-orders-complete-dialog.component';
import { LmoOrdersDivertDialogComponent } from '../lmo-orders-divert-dialog/lmo-orders-divert-dialog.component';
import { LmoReassignVendorComponent } from '../lmo-reassign-vendor/lmo-reassign-vendor.component';
import { ChoiceDialogComponent } from './../../../ui-components/choice-dialog/choice-dialog.component';
import { BillingAttachment } from './../../models/billingAttachment.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-order-details',
  styleUrls: ['./lmo-order-details.component.scss'],
  templateUrl: './lmo-order-details.component.html',
})
export class LmoOrderDetailsComponent implements OnInit, OnDestroy {
  @ViewChild('customerNoteModal', { static: true }) private customerNoteModal: TemplateRef<any>;
  private destroy$: Subject<null> = new Subject<null>();
  private dialogRef: MatDialogRef<any, any>;
  private viewState$: Observable<'view' | 'editing'>;
  public isEtaArrived = isEtaArrived;
  public isEtaNone = isEtaNone;
  public isEtaStaging = isEtaStaging;
  public isUnreachable = isUnreachable;
  public isOnline = isOnline;
  public isDriverAccepted = isDriverAccepted;
  public isDispatched = isDispatched;
  public isPendingGroup = isPendingGroup;
  public isPending = isPending;
  public isDriverRejected = isDriverRejected;
  public isInProgressGroup = isInProgressGroup;
  public isCompleteGroup = isCompleteGroup;
  public isComplete = isComplete;
  public isFinished = isFinished;
  public isEditable = isLmoEditable;
  public currentOrder$: Observable<Order>;
  public currentFracName$: Observable<string>;
  public backUrl$: Observable<string[]>;
  public canReassign$: Observable<boolean>;
  public orderId: Order;
  public uploading = '';
  private fileToUpload: File;
  private fileTypesWithUrl = ['pdf', 'svg', 'png', 'jpg', 'jpeg', 'png'];
  private allowedFileTypes = ['pdf', 'svg', 'png', 'jpg', 'jpeg', 'png', 'xls', 'xlsx', 'csv', 'doc', 'docx', 'ppt'];
  public fileLoading = false;
  public selectedFileName = '';
  public previewFileUrl = '';
  public bolAttachments: BillingAttachment[] = [];
  public truckTicketAttachments: BillingAttachment[] = [];
  public otherAttachments: BillingAttachment[] = [];
  public customerDriverRatingNote = new FormControl();
  public customerDriverRating = new FormControl();
  public ratingNoteLength$: Observable<number> = this.customerDriverRatingNote.valueChanges.pipe(
    startWith(''),
    map((value) => `${value}`.length),
  );
  public trackById = trackById;

  constructor(
    private orderService: LmoOrdersService,
    private fracService: LmoFracsService,
    private routerState: RouterStateService,
    private matDialog: MatDialog,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private fctvService: LmoFracFctvsService,
    public helperService: HelperService,
    public snackbar: MatSnackBar,
    private fileUploadService: FileUploadService,
    private errorHandler: ErrorHandlingService,
    private cd: ChangeDetectorRef,
  ) {}

  public ngOnInit() {
    this.currentOrder$ = this.orderService.currentOrder$;
    this.canReassign$ = combineLatest([this.currentOrder$, this.fctvService.currentFracFctvs$]).pipe(
      map(([currentOrder, fctvs]) =>
        fctvs.filter((fctv) => fctv.status === 'awarded' && fctv.truckingVendorId !== currentOrder.vendor.id),
      ),
      map((fctvs) => fctvs && fctvs.length > 0),
    );
    this.currentFracName$ = this.currentOrder$.pipe(
      switchMap((currentOrder) => this.fracService.getNameByFracId$(currentOrder.fracId)),
    );
    this.viewState$ = this.routerState.routerState$.pipe(
      map((routerState) => {
        const orderDetailState = routerState.queryParams['state'];
        if (orderDetailState === 'edit') {
          return 'editing';
        }
        return 'view';
      }),
    );
    this.backUrl$ = combineLatest([this.routerState.backUrl$, this.currentOrder$]).pipe(
      map(([backUrl, order]) => {
        if (order.uploadedFiles) {
          this.seperateFiles(order.uploadedFiles);
        }
        if (backUrl && backUrl.length) {
          return backUrl;
        }
        if (isCompleteGroup(order)) {
          return ['../', 'complete'];
        }

        if (isInProgressGroup(order)) {
          return ['../', 'in-progress'];
        }

        return ['../', 'pending'];
      }),
    );
    this.routerState.routerState$
      .pipe(pluck('queryParams', 'state'), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((orderDetailState) => {
        if (this.dialogRef) {
          this.matDialog.closeAll();
        }
        if (orderDetailState === 'cancel') {
          Promise.resolve().then(() => {
            this.dialogRef = this.matDialog.open(LmoOrdersCancelDialogComponent, {});
            this.dialogRef.afterClosed().subscribe(() => {
              this.router.navigate(['./'], { relativeTo: this.activatedRoute });
            });
          });
        } else if (orderDetailState === 'complete') {
          Promise.resolve().then(() => {
            this.dialogRef = this.matDialog.open(LmoOrdersCompleteDialogComponent, {
              maxWidth: '500px',
            });
            this.dialogRef.afterClosed().subscribe(() => {
              this.router.navigate(['./'], { relativeTo: this.activatedRoute });
            });
          });
        } else if (orderDetailState === 'reassign') {
          Promise.resolve().then(() => {
            this.dialogRef = this.matDialog.open(LmoReassignVendorComponent, {
              maxWidth: '500px',
              minWidth: '500px',
            });
            this.dialogRef.afterClosed().subscribe(() => {
              this.router.navigate(['./'], { relativeTo: this.activatedRoute });
            });
          });
        } else if (orderDetailState === 'diverted') {
          Promise.resolve().then(() => {
            this.currentOrder$.subscribe((value) => (this.orderId = value));
            this.dialogRef = this.matDialog.open(LmoOrdersDivertDialogComponent, {
              data: this.orderId,
              maxWidth: '500px',
              minWidth: '500px',
            });
            this.dialogRef.afterClosed().subscribe(() => {
              this.router.navigate(['./'], { relativeTo: this.activatedRoute });
            });
          });
        } else if (orderDetailState === 'verify') {
          Promise.resolve().then(async () => {
            this.dialogRef = this.matDialog.open(LmoOrdersVerifyLoadDetailsDialogComponent, {
              data: await this.currentOrder$.pipe(take(1)).toPromise(),
              maxWidth: '800px',
            });
            this.dialogRef.afterClosed().subscribe(() => {
              this.router.navigate(['./'], { relativeTo: this.activatedRoute });
            });
          });
        }
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.unsubscribe();
  }

  public onRate(event: { oldValue: number; newValue: number; starRating: StarRatingComponent }) {
    this.customerDriverRating.setValue(event.newValue);
    if (event.newValue === 1) {
      this.dialogRef = this.matDialog.open(this.customerNoteModal, { panelClass: 'w-1/3' });
    } else {
      this.orderService.rateDriver$(event.newValue, null).subscribe();
    }
  }

  public rateDriver() {
    if (this.customerDriverRatingNote.value.length < 10) {
      return;
    }
    if (this.dialogRef) {
      this.dialogRef.close();
    }
    this.orderService.rateDriver$(this.customerDriverRating.value, this.customerDriverRatingNote.value).subscribe();
  }

  public get isViewing$(): Observable<boolean> {
    return this.viewState$.pipe(map((state) => state === 'view'));
  }

  public get isEditing$(): Observable<boolean> {
    return this.viewState$.pipe(map((state) => state === 'editing'));
  }

  public seperateFiles(attachmentsList: BillingAttachment[]) {
    this.bolAttachments = [];
    this.truckTicketAttachments = [];
    this.otherAttachments = [];
    attachmentsList.forEach((file: BillingAttachment) => {
      switch (file.category) {
        case 'bol_image':
          this.bolAttachments.push(file);
          break;
        case 'ticket_image':
          this.truckTicketAttachments.push(file);
          break;
        case 'other_image':
          this.otherAttachments.push(file);
          break;
      }
    });
  }

  public onFileChange(event, currentOrderId: number, category: string) {
    if (event.target.files && event.target.files.length) {
      const [file] = event.target.files;
      this.fileToUpload = file;
      this.uploadFile(currentOrderId, category);
    }
  }

  private uploadFile(currentOrderId: number, category: string) {
    this.uploading = category;
    this.fileUploadService.uploadFile(currentOrderId, this.fileToUpload, category).subscribe(
      (result: BillingAttachment[]) => {
        this.uploading = '';
        this.snackbar.open('File uploaded Successfully', null, {
          duration: 5000,
        });
        this.seperateFiles(result);
        this.cd.detectChanges();
      },
      (err) => {
        console.error(err, 'Upload Failed', currentOrderId, this.fileToUpload);
        this.uploading = '';
        this.errorHandler.showError(`Upload Failed`, 5000);
      },
    );
  }

  public smallFileName(name: string) {
    const parts = name.split('/');
    return parts[parts.length - 1];
  }

  public getFileIcon(fileName: string) {
    const fileNameArray = fileName.toLowerCase().split('.');
    let extension = fileNameArray[fileNameArray.length - 1];
    if (this.allowedFileTypes.includes(extension)) {
      if (extension === 'xlsx') {
        extension = 'xls';
      }
      if (extension === 'docx') {
        extension = 'doc';
      }
      if (extension === 'jpeg') {
        extension = 'jpg';
      }
      return `assets/icons/${extension}.svg`;
    } else {
      return `assets/icons/other.png`;
    }
  }

  public downloadFile(file: BillingAttachment) {
    this.fileLoading = true;
    this.selectedFileName = file.fileName;
    this.fileUploadService.downloadFile(file.id, file.orderId).subscribe(
      (url) => {
        const fileNameArray = file.fileName.toLowerCase().split('.');
        if (this.fileTypesWithUrl.includes(fileNameArray[fileNameArray.length - 1])) {
          this.previewFileUrl = `${url}`;
          window.open(this.previewFileUrl, '_blank');
        } else {
          this.previewFileUrl = `https://view.officeapps.live.com/op/view.aspx?src=${url}`;
          window.open(this.previewFileUrl, '_blank');
        }
        this.fileLoading = false;
      },
      (err) => {
        this.fileLoading = false;
        this.errorHandler.showError('File Download Failed', 5000);
      },
    );
  }

  public deleteFile(file: BillingAttachment, event) {
    event.stopPropagation();
    const parts = file.fileName.split('/');
    const dialogRef = this.matDialog.open(ChoiceDialogComponent, {
      data: {
        button: ['Cancel', 'Remove'],
        context: `Are you sure you want to remove ${parts[parts.length - 1]} ?`,
        desc: '',
      },
      maxWidth: '968px',
      width: '30%',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.fileUploadService.removeFile(file.id, file.orderId).subscribe(
          (files) => {
            this.snackbar.open('File Removed Successfully', null, {
              duration: 5000,
            });
            this.seperateFiles(files);
            this.selectedFileName = '';
            this.previewFileUrl = '';
            this.cd.detectChanges();
          },
          (err) => {
            this.errorHandler.showError('File Removing Failed', 5000);
          },
        );
      }
    });
  }

  public openInWindow(fileUrl) {
    window.open(fileUrl, '_blank');
  }

  public isImage(fileName) {
    const imageExtensions = ['.jpeg', '.jpg', '.png'];
    return imageExtensions.some((ext) => fileName.includes(ext));
  }

  public getTotalLoadWeight() {
    return this.currentOrder$.pipe(
      map((order) => order.boxes.reduce((totalWeight, box) => totalWeight + box.actualLoadWeight, 0)),
    );
  }
}
